import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  contractsControllerChangeStatusV1,
  contractsControllerCreateV1,
  contractsControllerFindOneV1,
  contractsControllerListV1,
  contractsControllerRemoveV1,
  contractsControllerRenewalsV1,
  contractsControllerSummaryV1,
  contractsControllerUpdateV1,
} from '@/api/generated/contracts/contracts';
import type {
  ChangeContractStatusDto,
  ContractDto,
  CreateContractDto,
  UpdateContractDto,
} from '@/api/generated/model';
import { playerKeys } from '@/modules/players';
import { auditLogKeys } from '@/shared/lib/auditLogKeys';
import { contractKeys } from '@/shared/lib/contractKeys';
import { toContractsParams, toRenewalsParams, type ContractsSearch } from '../lib/contractsSearch';

export function useContractsQuery(search: ContractsSearch) {
  const params = toContractsParams(search);
  return useQuery({
    queryKey: contractKeys.list(params),
    queryFn: ({ signal }) => contractsControllerListV1(params, { signal }),
    placeholderData: (previous) => previous,
  });
}

export function useRenewalsQuery(search: ContractsSearch) {
  const params = toRenewalsParams(search);
  return useQuery({
    queryKey: contractKeys.renewals(params),
    queryFn: ({ signal }) => contractsControllerRenewalsV1(params, { signal }),
    placeholderData: (previous) => previous,
  });
}

export function useContractSummaryQuery() {
  return useQuery({
    queryKey: contractKeys.summary(),
    queryFn: ({ signal }) => contractsControllerSummaryV1({ signal }),
  });
}

export function useContractQuery(id: string) {
  return useQuery({
    queryKey: contractKeys.detail(id),
    queryFn: ({ signal }) => contractsControllerFindOneV1(id, { signal }),
    enabled: Boolean(id),
  });
}

/**
 * Shared success handling for every contract write.
 *
 * Every mutation on a contract or its children returns the complete
 * `ContractDto`, so the response is written straight into the detail cache — no
 * refetch, no flicker. The lists are invalidated because any write can move a
 * row between chips, tiles and buckets; the player's detail is invalidated
 * because the Contract tab and the summary columns read from here; the audit
 * trail because every write appends to it.
 */
export function useContractMutationEffects() {
  const queryClient = useQueryClient();

  return async (contract: ContractDto) => {
    queryClient.setQueryData(contractKeys.detail(contract.id), contract);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() }),
      queryClient.invalidateQueries({
        queryKey: auditLogKeys.forSubject('contract', contract.id),
      }),
      ...(contract.player
        ? [queryClient.invalidateQueries({ queryKey: playerKeys.detail(contract.player.id) })]
        : []),
    ]);
  };
}

export function useCreateContractMutation() {
  const onContract = useContractMutationEffects();
  return useMutation({
    mutationFn: (dto: CreateContractDto) => contractsControllerCreateV1(dto),
    onSuccess: onContract,
  });
}

export function useUpdateContractMutation(id: string) {
  const onContract = useContractMutationEffects();
  return useMutation({
    mutationFn: (dto: UpdateContractDto) => contractsControllerUpdateV1(id, dto),
    onSuccess: onContract,
  });
}

export function useChangeContractStatusMutation(id: string) {
  const onContract = useContractMutationEffects();
  return useMutation({
    mutationFn: (dto: ChangeContractStatusDto) => contractsControllerChangeStatusV1(id, dto),
    onSuccess: onContract,
  });
}

export function useDeleteContractMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contractsControllerRemoveV1(id),
    onSuccess: async (_result, id) => {
      // Dropped rather than invalidated: refetching a contract that no longer
      // exists only produces a 404.
      queryClient.removeQueries({ queryKey: contractKeys.detail(id) });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contractKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: playerKeys.details() }),
      ]);
    },
  });
}
